// src/pages/DisclaimerPage.jsx
import React, { useEffect, useState } from "react";
import NavBar from "../components/NavBar";

const MatrixDisclaimerBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById("matrix-disclaimer-bg");
    const context = canvas.getContext("2d");
    let width = window.innerWidth,
      height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;
    const katakana = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";
    const latin = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const alphabet = katakana + latin + nums;
    const fontSize = 16;
    const columns = width / fontSize;
    const rainDrops = new Array(Math.floor(columns)).fill(1);
    const draw = () => {
      context.fillStyle = "rgba(0, 0, 0, 0.05)";
      context.fillRect(0, 0, width, height);
      context.fillStyle = "#0F0";
      context.font = fontSize + "px monospace";
      for (let i = 0; i < rainDrops.length; i++) {
        const text = alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        context.fillText(text, i * fontSize, rainDrops[i] * fontSize);
        if (rainDrops[i] * fontSize > height && Math.random() > 0.975) {
          rainDrops[i] = 0;
        }
        rainDrops[i]++;
      }
    };
    const interval = setInterval(draw, 30);
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return (
    <canvas
      id="matrix-disclaimer-bg"
      className="fixed top-0 left-0 w-full h-full opacity-20 pointer-events-none"
    />
  );
};

const DisclaimerPage = () => {
  const [visible, setVisible] = useState(false);
  // Set effective date dynamically
  const effectiveDate = new Date().toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <section className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
      <NavBar />
      <MatrixDisclaimerBackground />
      <div className="relative z-10 container mx-auto px-4 py-16">
        {/* Card Container */}
        <div
          className={`max-w-4xl mx-auto bg-gray-800 bg-opacity-95 p-8 rounded-lg shadow-2xl transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <header className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-extrabold text-green-400 mb-4">
              Disclaimer
            </h1>
            <p className="text-lg text-gray-300">
              Please read this Disclaimer carefully. It governs your use of our website and services and outlines important limitations of liability.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Effective Date: {effectiveDate}
            </p>
          </header>
          <article className="space-y-6 text-base leading-7 text-gray-200">
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">1. General Disclaimer</h2>
              <p>
                The information contained on the AIOrbis website and within our services is provided for general informational purposes only. While we strive for accuracy, AIOrbis makes no representations or warranties of any kind, express or implied, regarding the completeness, accuracy, reliability, or availability of any information provided.
              </p>
              <p>
                All content is offered "as is" without any warranty. Your use of the information is at your sole risk. AIOrbis expressly disclaims any liability for errors or omissions in the content.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">2. No Professional Advice</h2>
              <p>
                Nothing on our website constitutes professional advice, whether legal, financial, or otherwise. Users should seek independent professional counsel before acting on any information obtained from AIOrbis.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">3. Limitation of Liability</h2>
              <p>
                Under no circumstances shall AIOrbis be liable for any direct, indirect, incidental, special, or consequential damages arising from your use of, or inability to use, our website or services. This includes any loss of profits, data, or other intangible losses.
              </p>
              <p>
                Our total liability for any claims arising from your use of our website shall not exceed the amount you have paid, if any, to access our services.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">4. External Links and Third-Party Content</h2>
              <p>
                Our website may include links to external sites that are not operated by AIOrbis. These links are provided solely for your convenience and do not constitute an endorsement of the content, products, or services available on these sites. We have no control over and assume no responsibility for the content or practices of these third-party websites.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">5. User Responsibility</h2>
              <p>
                You are solely responsible for any decisions or actions taken based on the information provided on our website. AIOrbis is not responsible for any loss or damage resulting from your reliance on any information contained herein.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">6. Changes to the Disclaimer</h2>
              <p>
                AIOrbis reserves the right to modify or update this Disclaimer at any time without prior notice. Any changes will be posted on this page, and your continued use of our website following the posting of changes constitutes your acceptance of such changes.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">7. No Warranty</h2>
              <p>
                AIOrbis disclaims all warranties, express or implied, including but not limited to the implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that our website or services will be uninterrupted, error-free, or free of viruses or other harmful components.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">8. Third-Party Services</h2>
              <p>
                Our website may integrate with or provide links to third-party services. AIOrbis is not responsible for the practices or content of these third parties and disclaims any liability for any damages arising from your use of them.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">9. Force Majeure</h2>
              <p>
                AIOrbis shall not be liable for any failure or delay in performance under this Disclaimer resulting from causes beyond its reasonable control, including but not limited to natural disasters, acts of government, failure of suppliers, or technical difficulties.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">10. Governing Law</h2>
              <p>
                This Disclaimer shall be governed by and construed in accordance with the laws of [Your Jurisdiction], without regard to its conflict of law principles. Any disputes arising out of or in connection with this Disclaimer shall be resolved exclusively in the courts located in [Your Location].
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">11. Entire Agreement</h2>
              <p>
                This Disclaimer, together with any other legal notices or agreements published on our website, constitutes the entire agreement between you and AIOrbis regarding your use of our website. It supersedes all prior agreements or understandings, whether written or oral.
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">12. Contact Information</h2>
              <p>
                If you have any questions regarding this Disclaimer, please contact us at:
              </p>
              <p>
                <strong>AIOrbis</strong><br />
                Email: legal@aiorbis.com<br />
                Address: 1234 Innovation Drive, Tech City, Country
              </p>
            </section>
            <section>
              <h2 className="text-2xl font-bold text-green-400 mb-2">13. Effective Date</h2>
              <p>
                This Disclaimer is effective as of {effectiveDate}. Your use of our website following this date indicates your acceptance of this Disclaimer.
              </p>
            </section>
          </article>
        </div>
      </div>
    </section>
  );
};

export default DisclaimerPage;
